import * as React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { ErrorDialog } from '../components/error-dialog';
import { ErrorCode } from '../enums/ErrorCode';
import { LanguageModelsResultModel } from '../models';
import { IUrlParameters } from '../models/urlParameters';
import ServicesContext, { ServicesCollection } from './servicesContext';

export interface IVinAppContext {
    vin: string;
    setVin: (vin: string) => void;
    vinLanguage: string;
    hasError: boolean;
    setError: (titleKey: string, descriptionKey: string) => void;
    initData: (urlParameters: IUrlParameters, callbackHandler: () => void) => void;
    setVinLanguage: (vinLanguage: string) => void,
    getVinLanguages: (vin: string, token:string) => Promise<LanguageModelsResultModel>;
    submitVinSearch: (vin: string, language: string) => void;
    urlParameters: IUrlParameters;
    clearUrlParameters: () => void;
    services: ServicesCollection
}

const defaultContext: IVinAppContext = {
    vin: null,
    setVin: (vin: string) => Function(),
    vinLanguage: null,
    hasError: false,
    setError: (titleKey: string, descriptionKey: string) => Function(),
    initData: (urlParameters: IUrlParameters, callbackHandler: () => void) => Function(),
    setVinLanguage: (vinLanguage: string) => Function(),
    getVinLanguages: (vin: string) => new Promise<LanguageModelsResultModel>((resolve, reject) => resolve(null)),
    submitVinSearch: (vin: string, language: string) => Function(),
    urlParameters: null,
    clearUrlParameters: () => Function(),
    services: null
}

export const VinAppContext: React.Context<IVinAppContext> = React.createContext<IVinAppContext>(defaultContext);

interface IVinContextProps extends Partial<RouteComponentProps<any>> {}

interface IVinContextState {
    hasError: boolean;
    errorTitle: string;
    errorDescription: string;
    vinLanguage: string;
    vin: string;
    vinLanguagesResult: LanguageModelsResultModel;
    urlParameters: IUrlParameters;
}

class VinContext extends React.Component<Partial<IVinContextProps>, IVinContextState> {
    static contextType = ServicesContext;
    context: React.ContextType<typeof ServicesContext>;

    constructor(props: any) {
        super(props);

        this.state = {
            hasError: false,
            errorTitle: "",
            errorDescription: "",
            vinLanguage: null,
            vin: null,
            vinLanguagesResult: null,
            urlParameters: null
        };
    }

    render(): any {
        return <VinAppContext.Provider value={{
            hasError: this.state.hasError,
            setError: (titleKey: string, descriptionKey: string) => this.setState({ ...this.state, hasError: true, errorTitle: titleKey, errorDescription: descriptionKey }),
            vin: this.state.vin,
            setVin: (vin: string) => this.setState({ ...this.state, vin: vin, vinLanguage: vin === null ? null : this.state.vinLanguage }),
            vinLanguage: this.state.vinLanguage,
            setVinLanguage: (vinLanguage: string) => { this.setState({ ...this.state, vinLanguage: vinLanguage }) },
            initData: this.initData,
            getVinLanguages: this.getVinLanguages,
            submitVinSearch: (vin: string, language: string) => this.setState({ ...this.state, vin: vin, vinLanguage: language }),
            urlParameters: this.state.urlParameters,
            clearUrlParameters: () => this.setState({ ...this.state, urlParameters: null }),
            services: this.context
        }}>
            <React.Fragment>
                {
                    this.state.hasError &&
                    <ErrorDialog
                        headingText={this.context.resourceProvider.getText(this.state.errorTitle)}
                        descriptionText={this.state.errorDescription ? this.context.resourceProvider.getText(this.state.errorDescription) : null}
                        onClose={() => this.setState({ ...this.state, hasError: false, errorTitle: "", errorDescription: "" })}
                    />
                }
                {this.props.children}
            </React.Fragment>
        </VinAppContext.Provider>    
    }

    initData = (urlParameters: IUrlParameters, callbackHandler: () => void) => {
        this.setState({
            ...this.state,
            urlParameters: urlParameters,
            vin: urlParameters.vin,
            vinLanguage: urlParameters.manualLang
        }, callbackHandler);
	}

    getVinLanguages = async (vinCode: string, token: string): Promise<LanguageModelsResultModel> => {
        if (vinCode == this.state.vin &&
            this.state.vinLanguagesResult &&
            this.state.vinLanguagesResult.results.length > 0 &&
            this.state.vinLanguagesResult.errorCode == ErrorCode.NoError) {
            return this.state.vinLanguagesResult;
		}
        try {
            const result = await this.context.apiService.getLanguagesForVin(vinCode, token);
            this.setState({
                vinLanguagesResult: result
            });
            return result;
        }
        catch (error) {
            this.setState({ ...this.state, hasError: true, errorTitle: 'Search_Error_Title', errorDescription: 'Search_Error_Description' });
            throw error;
		}
    }
}

export default VinContext;